import { Fragment, useCallback, useRef } from 'react';

import { cn, ia } from 'lib/helpers/utility';

import { useBreadcrumbContext } from '../../context/BreadcrumbContext';
import useOverflow from '../../hooks/useOverflow';

import Ellipsis from './components/Ellipsis';
import Indicator from './components/Indicator';
import Placeholder from './components/Placeholder';
import Step from './components/Step';

/** @type {import('react').FC<import('../../lib/propTypes').BreadcrumbListProps>} */

const BreadcrumbList = ({
  className,
  textClassName,
  indicator = 'slash',
  isLoading,
  labelData,
  activeClassName,
  inactiveClassName,
  onStepClick,
  trimSteps = false,
  lockNext,
  disableClick,
  noBrackets
}) => {
  const { steps, step, setStep, setStepWithTrim } = useBreadcrumbContext();
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const setItemRef = useCallback((index, el) => {
    itemRefs.current[index] = el;
  }, []);

  const stepsCount = steps?.length || 0;

  const { visibleStartIndex } = useOverflow({
    containerRef,
    itemRefs,
    stepsCount
  });

  const handleStepClick = (index) => {
    if (onStepClick) {
      onStepClick(index, steps[index]);
      return;
    }
    if (trimSteps || index < step) {
      setStepWithTrim(index);
    } else {
      setStep(index);
    }
  };

  if (!ia(steps)) return null;

  const hasOverflow = visibleStartIndex > 0;
  const overflowSteps = hasOverflow ? steps.slice(0, visibleStartIndex) : [];

  return (
    <div
      ref={containerRef}
      className={cn('flex w-full min-w-0 items-center overflow-hidden', className)}>
      {isLoading ? (
        <Placeholder stepsLength={stepsCount} indicator={indicator} />
      ) : (
        <>
          {hasOverflow && (
            <Ellipsis steps={overflowSteps} onStepClick={handleStepClick} indicator={indicator} />
          )}
          {steps.map((s, index) => (
            <Fragment key={index}>
              <div
                ref={(el) => setItemRef(index, el)}
                className={cn('flex shrink-0 items-center', index < visibleStartIndex && 'hidden')}>
                <Step
                  step={s}
                  index={index}
                  isActive={index === step}
                  textClassName={textClassName}
                  activeClassName={activeClassName}
                  inactiveClassName={inactiveClassName}
                  noBrackets={noBrackets}
                  disableClick={disableClick}
                  lockNext={lockNext}
                  items={{ steps, step }}
                  labelData={labelData}
                  onClick={handleStepClick}
                />
                {index !== stepsCount - 1 && <Indicator icon={indicator} />}
              </div>
            </Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export default BreadcrumbList;
